import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { api } from '@/lib/api';

export default function NewCrewForm({ user, crew, onClose, onCreated }) {
  const editing = !!crew;
  const [vessels, setVessels] = useState([]);
  const [form, setForm] = useState({
    first_name: crew?.first_name || '', last_name: crew?.last_name || '',
    crew_id: crew?.crew_id || '', nationality: crew?.nationality || '',
    position: crew?.position || crew?.rank || '', department: crew?.department || '',
    vessel: crew?.vessel || (user.role === 'vessel_hr' ? (user.vessel || '') : ''),
    hire_date: crew?.hire_date || '', completed_contracts: crew?.completed_contracts ?? 0,
    contract_end: crew?.contract_end || '',
  });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api('/vessels').then((vs) => {
      const names = vs.map((v) => v.name);
      setVessels(names);
      if (!form.vessel && names.length) setForm((f) => ({ ...f, vessel: names[0] }));
    });
    /* eslint-disable-next-line */
  }, []);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await api(editing ? `/crew/${crew.id}` : '/crew', {
        method: editing ? 'PUT' : 'POST',
        body: JSON.stringify({ ...form, completed_contracts: Number(form.completed_contracts) || 0 }),
      });
      onCreated?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <form className="drawer" onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="new-crew-drawer">
        <div className="drawer-head">
          <div>
            <span className="eyebrow">{editing ? 'EDIT CREW MEMBER' : 'NEW CREW MEMBER'}</span>
            <h2>{editing ? 'Edit crew record' : 'Add crew record'}</h2>
          </div>
          <button type="button" className="icon-btn" data-testid="new-crew-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="drawer-body stack">
          <div className="row-two">
            <label className="field"><span>First name</span>
              <input required data-testid="new-crew-first" value={form.first_name} onChange={(e) => set('first_name', e.target.value)} />
            </label>
            <label className="field"><span>Last name</span>
              <input required data-testid="new-crew-last" value={form.last_name} onChange={(e) => set('last_name', e.target.value)} />
            </label>
          </div>
          <div className="row-two">
            <label className="field"><span>Crew ID</span>
              <input data-testid="new-crew-id" value={form.crew_id} onChange={(e) => set('crew_id', e.target.value)} placeholder="e.g. CR-2058" />
            </label>
            <label className="field"><span>Nationality</span>
              <input data-testid="new-crew-nationality" value={form.nationality} onChange={(e) => set('nationality', e.target.value)} placeholder="e.g. Filipino" />
            </label>
          </div>
          <div className="row-two">
            <label className="field"><span>Position</span>
              <input required data-testid="new-crew-position" value={form.position} onChange={(e) => set('position', e.target.value)} placeholder="e.g. Chief Steward" />
            </label>
            <label className="field"><span>Department</span>
              <input data-testid="new-crew-department" value={form.department} onChange={(e) => set('department', e.target.value)} placeholder="e.g. Hotel Operations" />
            </label>
          </div>
          <label className="field"><span>Vessel</span>
            <select required data-testid="new-crew-vessel" value={form.vessel} onChange={(e) => set('vessel', e.target.value)} disabled={user.role === 'vessel_hr'}>
              {form.vessel && !vessels.includes(form.vessel) && <option value={form.vessel}>{form.vessel}</option>}
              {vessels.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </label>
          <div className="row-two">
            <label className="field"><span>Original hire date</span>
              <input required data-testid="new-crew-hire-date" type="date" value={form.hire_date} onChange={(e) => set('hire_date', e.target.value)} />
            </label>
            <label className="field"><span>Completed contracts</span>
              <input data-testid="new-crew-contracts" type="number" min="0" value={form.completed_contracts} onChange={(e) => set('completed_contracts', e.target.value)} />
            </label>
          </div>
          <label className="field"><span>Current contract end (optional)</span>
            <input data-testid="new-crew-contract-end" type="date" value={form.contract_end} onChange={(e) => set('contract_end', e.target.value)} />
          </label>
          {error && <div data-testid="new-crew-error" className="error">{error}</div>}
          <button className="primary-btn" data-testid="new-crew-submit" type="submit" disabled={busy}>
            {busy ? 'Saving…' : (editing ? 'Save changes' : 'Add crew member')}
          </button>
        </div>
      </form>
    </div>
  );
}
